import React, { useState } from 'react';
import { Text, View, Pressable, TouchableOpacity, ActivityIndicator} from 'react-native';
import { GestureDetector, Gesture } from 'react-native-gesture-handler';
import Animated, { useAnimatedStyle, useSharedValue, withTiming, interpolate, Extrapolation, runOnJS } from 'react-native-reanimated';
import { impactMedium } from '@/utils/haptic';
import { showAlert } from '@/utils/showAlert';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from './Unicon';

const ActionCard = props => {
const [isLoading, setIsLoading] = useState(false)

    const { 
        title,
        subtitle,
        icon = null,
        timeStamp = null,
        confirmAction = null,
        enableSwipeLeft = true,
        leftColorVariant = "",
        leftIcon = "checkmark-outline",
        leftAction = () => {},
        leftActionRemovesCard = false,
        enableSwipeRight = true,
        rightColorVariant = "",
        rightIcon = "close-outline",
        rightAction = () => {},
        rightConfirmation = "",
        rightActionRemovesCard = true,
        actionType = "",
        maxLines = 0,
        onLongPress = () => {},
        menu = null
    } = props;


    const heightAnim = useSharedValue({
        maxHeight: 200,
        paddingVertical: 5
    });
    const animationStyle = useAnimatedStyle(() => {
        return {
            maxHeight: withTiming(heightAnim.value.maxHeight, {duration:300}),
            paddingVertical: withTiming(heightAnim.value.paddingVertical, {duration:300})
        }
    });


    const removeCard = () => {
        heightAnim.value = {
            maxHeight: 0,
            paddingVertical: 0
        };
    }


    const BUTTON_SIZE = 32;
    const END_POSITION = BUTTON_SIZE + 10;
    const OVERSHOOT_SCALE = (END_POSITION - BUTTON_SIZE) / BUTTON_SIZE;
    const direction = useSharedValue(0); //-1 = left, 0 = center, 1 = right
    const position = useSharedValue(0);
    const actionTriggered = useSharedValue(false);


    const triggerRightAction = () => {
        if(rightActionRemovesCard){
            if(rightConfirmation){
                showAlert({
                    title: rightConfirmation,
                    subtitle: "",
                    isDestructive: true,
                    onCancel: () => {
                        position.value = withTiming(0, { duration: 200 });
                        actionTriggered.value = false;
                    },
                    onConfirm: () => {
                        //Move the card off screen
                        position.value = withTiming(-(1000), { duration: 200 });
                        runOnJS(removeCard)();

                        setTimeout(() => {
                            runOnJS(rightAction)()
                        }, 300)
                    },
                    confirmText: "Delete"
                })
            }
            else{
                //Move the card of screen
                position.value = withTiming(-(1000), { duration: 200 });
                runOnJS(removeCard)();

                setTimeout(() => {
                    runOnJS(rightAction)()
                }, 300)
            }
        }
        else{
            runOnJS(rightAction)()
            //If we don't remove the card, recenter it.
            position.value = withTiming(0, { duration: 200 });
            actionTriggered.value = false;
        }
    }

    const gestureOnUpdate = (e) => {
        if (direction.value == 0) {
            if((!enableSwipeLeft && e.translationX > 0) || (!enableSwipeRight && e.translationX < 0)){
                return;
            }
            position.value = e.translationX;
        } else if(direction.value == 1) {
            position.value = -(END_POSITION) + e.translationX;
        } else {
            position.value = END_POSITION + e.translationX;
        }

        //Initiate the action if the user overshoots double 
        if(!actionTriggered.value && position.value > (END_POSITION * 3)){
            actionTriggered.value = true;
            runOnJS(impactMedium)()
        }
        else if(!actionTriggered.value && position.value < -(END_POSITION * 2)){
            actionTriggered.value = true;
            runOnJS(impactMedium)()
        }

        //Undo the trigger if the user pulls it back to the center
        if(actionTriggered.value && (position.value < (END_POSITION * 2) && position.value > -(END_POSITION * 2))){
            actionTriggered.value = false;
        }
    }


    const gestureOnEnd = () => {
        if (position.value > END_POSITION) {
            //Pulling towards Right (revealing Left Action)
            if(actionTriggered.value){
                direction.value = 0;

                if(leftActionRemovesCard){
                    //Move the card of screen
                    position.value = withTiming(1000, { duration: 200 });
                    runOnJS(removeCard)();

                    setTimeout(() => {
                        runOnJS(leftAction)()
                    }, 300)
                }
                else{
                    //If we don't remove the card, recenter it.
                    position.value = withTiming(0, { duration: 200 });
                    actionTriggered.value = false;
                    runOnJS(leftAction)();
                }
            }
            else{
                position.value = withTiming(END_POSITION, { duration: 200 });
                direction.value = -1;
            }

        } 
        else if (position.value < -(END_POSITION)) {
            //Pulling towards Left (revealing Right Action)
            if(actionTriggered.value){
                direction.value = 0;
                runOnJS(triggerRightAction)();
            }
            else{
                position.value = withTiming(-(END_POSITION), { duration: 200 });
                direction.value = 1;
            }

        } else {
            //Centered
            position.value = withTiming(0, { duration: 200 });
            direction.value = 0;
        }
    }

    const panGesture = Gesture.Pan()
        // .minDistance(10)
        .runOnJS(true)
        .activeOffsetX([-5, 5])
        .onUpdate(gestureOnUpdate)
        .onEnd(gestureOnEnd)

    const translateStyle = useAnimatedStyle(() => ({
        transform: [{ translateX: position.value }],
    }));



    const RightSwipe = () => {
        const scaleStyle = useAnimatedStyle(() => {
            const scale = interpolate(position.value, [-(END_POSITION + 10), 0], [(1 + OVERSHOOT_SCALE), 0], { extrapolateRight: Extrapolation.CLAMP, extrapolateLeft: Extrapolation.CLAMP });
        
            return {
              transform: [{ scale: (actionTriggered.value && heightAnim.value.maxHeight == 0) ? withTiming(0, {duration:200}) : scale }],
              opacity: scale
            };
        });
        
        return (
            <Animated.View style={[scaleStyle, styles.swipeContainer(false)]}>
                <TouchableOpacity style={styles.swipeButton(rightColorVariant || "error", BUTTON_SIZE)} onPress={() => {
                    actionTriggered.value = true;
                    triggerRightAction();
                }}>
                    <Unicon name={rightIcon} colorVariant={"on" + rightColorVariant || "error"} size={22}/> 
                </TouchableOpacity>
            </Animated.View>
        )
    }

    const LeftSwipe = () => {
        const scaleStyle = useAnimatedStyle(() => {
            const scale = interpolate(position.value, [0, END_POSITION + 10], [0, (1 + OVERSHOOT_SCALE)], { extrapolateRight: Extrapolation.CLAMP, extrapolateLeft: Extrapolation.CLAMP });
        
            return {
              transform: [{ scale: (actionTriggered.value && heightAnim.value.maxHeight == 0) ? withTiming(0, {duration:200}) : scale }],
              opacity: scale
            };
        });

        
        return (
            <Animated.View style={[scaleStyle, styles.swipeContainer(true)]}>
                <TouchableOpacity style={styles.swipeButton(leftColorVariant || "success", BUTTON_SIZE)} onPress={leftAction}>
                    <Unicon name={leftIcon} colorVariant={"on" + leftColorVariant || "success"} size={25}/> 
                </TouchableOpacity>
            </Animated.View>
        )
    }

    const dateTimeStamp = new Date(timeStamp);

    return (
        <GestureDetector gesture={panGesture}>
            <Animated.View style={[animationStyle]}>
                {enableSwipeLeft && <LeftSwipe />}
                {enableSwipeRight && <RightSwipe />}
                <Animated.View style={[translateStyle]}>
                    <Pressable testID={`ActionCard-${title}`} hitSlop={5} onLongPress={onLongPress} onPress={async () => {
                        if(confirmAction){
                            setIsLoading(true)
                            await confirmAction()
                            setIsLoading(false)
                        }
                    }}>
                        <View style={styles.card(actionType)}>

                            { icon && 
                                <View style={styles.iconContainer}>
                                    { icon }
                                </View>
                            }

                            <View style={styles.contentContainer}>
                                <Text numberOfLines={1} style={styles.header}>{title}</Text>
                                { subtitle && <Text numberOfLines={maxLines} style={styles.sub_header}>{subtitle}</Text> }
                                { timeStamp && <Text style={styles.sub_header}>{dateTimeStamp.toLocaleString('en-US', {month:"short", day:'numeric', year:"numeric", hour:"numeric", minute:"2-digit"})}</Text> }
                            </View>

                            <View style={styles.loadingContainer}>
                                {isLoading && <ActivityIndicator size={'small'} />}
                            </View>
                        </View>
                    </Pressable>

                    { (menu && !isLoading) &&
                        <View testID={`ActionCardMenu-${title}`} style={styles.menuContainer}>
                            {menu}
                        </View>
                    }
                </Animated.View>
            </Animated.View>
        </GestureDetector>
    )
}

const styles = StyleSheet.create(({colors, fonts, shadows}) => ({
    header: {
        fontSize:15,
        textAlign:"left",
        fontFamily: fonts.bold,
        color: colors.text,
        width:"95%"
    },
    sub_header: {
        marginTop: 5,
        fontSize:12,
        fontStyle:"italic",
        textAlign:"left",
        color: colors.textSecondary
    },
    menuContainer:{
        position:"absolute", 
        top:8, 
        right:15, 
        zIndex:10
    },
    loadingContainer:{
        position:"absolute", 
        right:20, 
        top:"52%"
    },
    iconContainer:{
        alignItems:"center", 
        justifyContent:'center', 
        width:35
    },
    contentContainer:{
        marginLeft:12, 
        paddingRight:25, 
        flexGrow:1
    },
    card: (actionType) => ({
        flexDirection:'row', 
        alignItems:'center', 
        borderRadius:8,
        borderWidth:1,
        borderColor:colors.border,
        borderLeftWidth:actionType ? 3 : 1,
        borderLeftColor:actionType ? colors[actionType] : colors.border,
        padding: 10,
        backgroundColor: colors.surface,
        boxShadow:shadows.card
    }),
    swipeContainer: (isLeft) => ({
        justifyContent:"center", 
        position:"absolute", 
        right: isLeft ? undefined : 5,
        left: isLeft ? 5 : undefined,
        height:"100%", 
        marginTop:5 
    }),
    swipeButton: (backgroundColor, size) => ({
        backgroundColor: colors[backgroundColor], 
        height: size, 
        width: size, 
        alignItems: "center", 
        justifyContent: "center", 
        borderRadius: size
    })
}));

export default React.memo(ActionCard);